import type { LucideIcon } from "lucide-react";
import {
  Building2,
  Cpu,
  FlaskConical,
  HardHat,
  Headphones,
  Home as HomeIcon,
  Megaphone,
  Phone,
  Scale,
  ShoppingBag,
  Truck,
  Shield,
} from "lucide-react";

const bankImg =
  "https://images.unsplash.com/photo-1632507127789-eb70cc8757af?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxiYW5raW5nJTIwZmluYW5jZSUyMHRyYWRpbmclMjBmbG9vciUyMHByb2Zlc3Npb25hbHxlbnwxfHx8fDE3Nzc5MTUwMTV8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral";
const pharmaImg =
  "https://images.unsplash.com/photo-1766297246958-b6ca17b56fb7?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwaGFybWFjZXV0aWNhbCUyMHJlc2VhcmNoJTIwbGFiJTIwc2NpZW50aXN0fGVufDF8fHx8MTc3NzkxNTAxNHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral";
const cyberImg =
  "https://images.unsplash.com/photo-1751448555253-f39c06e29d82?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjeWJlcnNlY3VyaXR5JTIwc2hpZWxkJTIwcHJvdGVjdGlvbiUyMGRpZ2l0YWx8ZW58MXx8fHwxNzc3OTE1MDE1fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral";

import type { CctvHighlightContent, WebcamHighlightContent } from "./planCatalog";

export type IndustryTopic = {
  slug: string;
  label: string;
  pageTitle: string;
  metaDescription: string;
  icon: LucideIcon;
  iconBg: string;
  accentColor: "teal" | "emerald";
  variant: "featured" | "standard";
  headline: string;
  description: string;
  keyRisks: string[];
  /** CCTV+ add-on use cases for this industry (Pro / Enterprise only). */
  cctvHighlight: CctvHighlightContent;
  /** Optional Webcam Capture identity verification angle. */
  webcamHighlight?: WebcamHighlightContent;
  image?: string;
  imageAlt?: string;
  marketingCta: {
    titleBefore: string;
    titleAccent: string;
    description: string;
  };
};

export const industryTopics: IndustryTopic[] = [
  {
    slug: "banking",
    label: "Banking & Fintech",
    pageTitle: "Banking & fintech session evidence",
    metaDescription:
      "Policy-scoped visual trails for wires, trading workstations, and wealth operations. DeskReplay forensic desktop session recording for fraud and audit readiness in banking and fintech.",
    icon: Building2,
    iconBg: "bg-teal-600",
    accentColor: "teal",
    variant: "featured",
    headline: "Stop wire disputes before they become lawsuits.",
    description:
      "In wealth management, trade execution, and institutional banking, every operator action carries fiduciary weight. DeskReplay generates tamper-proof forensic evidence to support your FINRA, SEC, and MiFID II audit requirements. Policy-scoped capture on teller, advisor, and trading workstations gives fraud and compliance teams tamper-evident replay without reconstructing chat logs.",
    keyRisks: [
      "Wire instruction disputes and fraud liability",
      "Trade execution errors and order management",
      "Know Your Customer (KYC) process deviations",
      "Anti-Money Laundering (AML) procedure adherence",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, branch lobby, ATM, and vault-adjacent NVR streams can be indexed alongside teller and advisor desktop recordings for cross-channel incident reconstruction.",
      useCases: [
        "ATM skimming and card-trapping investigations with lobby camera timelines",
        "Branch robbery or after-hours intrusion post-incident search",
        "Correlating suspicious wire activity with who was at the counter on camera",
        "Vault-adjacent access reviews paired with workstation audit trails",
        "Regulator and internal audit samples with forensic NVR export",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture adds identity snapshots at teller, advisor, and trading workstations so fraud and audit teams can confirm the logged-in banker was at the keyboard during wire and trade disputes.",
      useCases: [
        "Wire room shared PCs where FINRA-style reviews need desk presence, not only OMS replay",
        "Branch moonlighting and credential-sharing investigations on hybrid advisor laptops",
        "Pro random intervals across shifts; Enterprise triggers when wire or trading apps gain focus",
        "External USB webcams on dual-monitor wealth desks when the built-in camera misses the operator",
      ],
    },
    image: bankImg,
    imageAlt: "Banking trading floor",
    marketingCta: {
      titleBefore: "Ready to stop ",
      titleAccent: "banking fraud narratives cold?",
      description:
        "We'll focus on wire rooms, OMS, and CRM workflows where disputes cost seven figures, and show how capture stays proportional.",
    },
  },
  {
    slug: "pharma",
    label: "Pharma & CRO",
    pageTitle: "Pharma, CRO & GxP workstation evidence",
    metaDescription:
      "GxP-aligned visual audit trails for clinical and manufacturing workstations. DeskReplay session recording for pharma, CRO, and 21 CFR Part 11 audit readiness.",
    icon: FlaskConical,
    iconBg: "bg-emerald-600",
    accentColor: "emerald",
    variant: "featured",
    headline: "Built to support 21 CFR Part 11 workflows and accelerate GxP audit-readiness.",
    description:
      "Clinical data entry errors in pharma and contract research organizations can trigger FDA warning letters and trial invalidations worth hundreds of millions. DeskReplay provides GxP-aligned visual audit trails for every data entry event. Application-scoped capture on EDC, LIMS, and manufacturing HMIs documents what operators saw during data entry and batch workflows.",
    keyRisks: [
      "Clinical data entry deviation documentation",
      "LIMS and EDC system access audit trails",
      "SOPs for data integrity and version control",
      "Evidence to support 21 CFR Part 11 electronic record reviews",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, clean room, manufacturing line, and facility NVR streams can be indexed alongside GxP workstation capture for deviation investigations and audit responses.",
      useCases: [
        "Manufacturing deviation investigations with synchronized camera and HMI playback",
        "Clean room access and gowning procedure verification",
        "Material handling disputes on the production floor",
        "Regulatory audit samples that combine batch records with floor camera evidence",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture supports GxP and data-integrity investigations by confirming which operator was at the EDC, LIMS, or HMI workstation when screen replay documents a deviation.",
      useCases: [
        "Clinical data entry disputes when user IDs in the system do not match floor reality",
        "Manufacturing batch review stations with shared logins on validated PCs",
        "CRO and sponsor oversight of remote monitors during trial data cleaning windows",
        "Enterprise startup snapshots on lab laptops after sign-in at the beginning of a batch run",
      ],
    },
    image: pharmaImg,
    imageAlt: "Pharma research laboratory",
    marketingCta: {
      titleBefore: "Ready to defend ",
      titleAccent: "data integrity under scrutiny?",
      description:
        "Map capture to EDC, LIMS, and manufacturing HMIs, so QA and QA auditors see exactly what operators saw.",
    },
  },
  {
    slug: "legal",
    label: "Legal & Professional Services",
    pageTitle: "Legal & professional services evidence",
    metaDescription:
      "Court-ready chain of custody for matter teams, billing, and e-discovery. DeskReplay privileged workstation session recording for legal and professional services.",
    icon: Scale,
    iconBg: "bg-teal-600",
    accentColor: "teal",
    variant: "featured",
    headline: "Court-ready evidence that supports chain-of-custody audit narratives.",
    description:
      "Legal firms handling sensitive documents, matter management, and client communications need to demonstrate process integrity. DeskReplay provides the visual evidence layer that text logs cannot. Policy-scoped capture on matter management, billing, and document systems gives counsel tamper-evident exports aligned to privilege boundaries. Enterprise teams can add mic plus system session audio when counsel approves proportionate monitoring.",
    keyRisks: [
      "Document handling and privileged information access",
      "Matter billing and time entry accuracy",
      "Client communication and instruction compliance",
      "E-discovery process documentation",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, law firms and litigation support teams can index office lobby, records room, deposition suite, and parking NVR streams alongside matter workstation capture for premises incidents.",
      useCases: [
        "Premises liability and slip-and-fall incidents at firm offices",
        "After-hours records room and file storage access reviews",
        "Reception and client visit disputes with searchable lobby footage",
        "Subpoena and law enforcement responses with forensic ZIP export",
        "Multi-office firms searching camera archives across locations in one query",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture adds proportionate identity checks at matter workstations when billing, privilege, or document-handling disputes require knowing who was at the keyboard.",
      useCases: [
        "Time-entry and billing fraud reviews on shared paralegal stations",
        "Document production PCs where wrong-person access could affect privilege claims",
        "Hybrid associate laptops during client-mandated audit samples",
        "Pairing e-discovery screen segments with identity stills under counsel-approved scope",
      ],
    },
    image: cyberImg,
    imageAlt: "Legal technology security",
    marketingCta: {
      titleBefore: "Ready for privilege-aware ",
      titleAccent: "visual reconstruction?",
      description:
        "We'll align RBAC and retention to matter boundaries, so counsel gets defensible exports without over-collecting.",
    },
  },
  {
    slug: "medical-devices",
    label: "Medical Devices",
    pageTitle: "Medical device quality & audit trails",
    metaDescription:
      "DHF-adjacent workstation evidence for medical device quality, complaint handling, and FDA-style audits with DeskReplay desktop session recording.",
    icon: FlaskConical,
    iconBg: "bg-emerald-600",
    accentColor: "emerald",
    variant: "standard",
    headline: "Close the loop between design reviews and what operators actually did.",
    description:
      "From complaint intake to design changes, medical device manufacturers must show disciplined execution. Pair existing QMS logs with visual playback on validated workstations where human execution is the control.",
    keyRisks: [
      "Design history file traceability for software-heavy devices",
      "Post-market surveillance and complaint investigation",
      "Training effectiveness evidence for complex tooling",
      "Supplier and contract manufacturer oversight",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, medical device manufacturers can index clean room, packaging line, sterilization, and warehouse NVR streams alongside QMS workstation capture for audits and post-market investigations.",
      useCases: [
        "Complaint investigations with synchronized floor camera and HMI playback",
        "Contract manufacturer and supplier site oversight via searchable NVR archives",
        "Training effectiveness reviews at validated production stations",
        "Packaging and labeling line deviation documentation for FDA-style audits",
        "Distribution center damage and handling disputes with forensic export",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture closes the loop on complaint and CAPA reviews by confirming the quality engineer at the QMS workstation matches the electronic signature on the record.",
      useCases: [
        "Post-market complaint intake when multiple specialists share a validated review PC",
        "Supplier oversight of contract manufacturer data entry during FDA-style audits",
        "Training effectiveness checks at software validation stations",
        "Best quality profiles (Enterprise) on external webcams at packaging line supervisor desks",
      ],
    },
    marketingCta: {
      titleBefore: "Ready for audits that ask ",
      titleAccent: "\"show me the session\"?",
      description:
        "We'll scope capture to validated apps and environments, proportionate to FDA-style expectations, not blanket monitoring.",
    },
  },
  {
    slug: "insurance",
    label: "Insurance",
    pageTitle: "Insurance claims & underwriting evidence",
    metaDescription:
      "Claims and SIU investigations with adjuster desktop session recording. DeskReplay for carriers, TPAs, and reinsurers.",
    icon: Scale,
    iconBg: "bg-teal-600",
    accentColor: "teal",
    variant: "standard",
    headline: "When reserves and reputations hinge on a single adjustment.",
    description:
      "Claims handling blends regulated timelines with subjective judgment calls. DeskReplay preserves what adjusters saw in core systems, reducing fraud leapfrogging and accelerating good-faith investigations.",
    keyRisks: [
      "Claims processing errors and reserve disputes",
      "SIU and fraud investigation documentation",
      "Third-party administrator oversight",
      "Reinsurance audit and ceding verification",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, SIU and property teams can search indexed NVR footage from loss sites and partner locations alongside adjuster desktop sessions.",
      useCases: [
        "Property damage claim verification with timestamped exterior and interior camera search",
        "Staged loss and fraud pattern investigation across multiple policy locations",
        "Contractor and restoration vendor oversight at active claim sites",
        "Reinsurance audit samples with exportable chain-of-custody camera evidence",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture helps SIU and claims leaders confirm adjusters at managed workstations match the accounts tied to disputed reserves and fraud referrals.",
      useCases: [
        "Reserve and bad-faith disputes when screen replay shows correct system use but presence is questioned",
        "TPA oversight of offshore claim handlers on shared floor pods",
        "Random interval identity samples during regulator or reinsurer audit windows",
        "Moonlighting investigations pairing claim system access with desk presence stills",
      ],
    },
    marketingCta: {
      titleBefore: "Ready to replay ",
      titleAccent: "the claim file, not the story?",
      description:
        "We'll connect policy triggers to the claims desktop apps your teams live in every day.",
    },
  },
  {
    slug: "telecom",
    label: "Telecommunications",
    pageTitle: "Telecommunications CSR & OSS/BSS evidence",
    metaDescription:
      "OSS/BSS desktop session recording for porting, billing, and fraud disputes. DeskReplay for regulated carriers and MVNOs.",
    icon: Phone,
    iconBg: "bg-emerald-600",
    accentColor: "emerald",
    variant: "standard",
    headline: "Subscriber disputes deserve operator-visible truth.",
    description:
      "Billing adjustments, port-outs, and fraud holds involve dozens of systems. Visual replay clarifies scripting adherence in CSR and provisioning consoles without exposing unrelated subscriber data. System audio streaming on Pro preserves hold, transfer, and scripting cues that never appear on screen.",
    keyRisks: [
      "CSR scripting and promotional discount disputes",
      "Porting and provisioning errors",
      "Fraud and SIM-swap investigations",
      "Workforce compliance in outsourced call centers",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, telecom carriers and MVNOs can index retail stores, data centers, central offices, and outsourced call floors to correlate NVR footage with OSS/BSS desktop capture during fraud and service disputes.",
      useCases: [
        "Retail store SIM-swap and identity verification disputes with in-store camera search",
        "Data center physical access reviews during outage or security incidents",
        "Tower site, central office, and facility vandalism investigations",
        "Outsourced call center floor cameras paired with CSR desktop compliance reviews",
        "Regulator and law enforcement requests with chain-of-custody NVR export",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture supports CSR and fraud desk accountability by verifying the agent at the OSS/BSS console matches the employee ID on porting, billing, and SIM-swap actions.",
      useCases: [
        "Outsourced call center floors with seat sharing and client SLA identity requirements",
        "Retail store back-office PCs used for subscriber account changes",
        "Enterprise triggers when provisioning or fraud-hold applications open during investigations",
        "External USB webcams on agent workstations with raised monitors blocking built-in cameras",
      ],
    },
    marketingCta: {
      titleBefore: "Ready to align QA with ",
      titleAccent: "what agents actually clicked?",
      description:
        "Trigger capture on billing and provisioning stacks, not every idle desktop minute.",
    },
  },
  {
    slug: "government",
    label: "Government & Public",
    pageTitle: "Government & public-sector accountability",
    metaDescription:
      "FOIA-adjacent accountability with policy-scoped desktop session recording on government networks. DeskReplay self-hosted for public sector.",
    icon: Building2,
    iconBg: "bg-teal-600",
    accentColor: "teal",
    variant: "standard",
    headline: "Demonstrate procedural fidelity when oversight calls.",
    description:
      "Whether processing benefits, permits, or procurement actions, agencies need tamper-evident evidence that staff followed approved systems of record, without wholesale surveillance. All processing stays on-premise inside your network.",
    keyRisks: [
      "Administrative procedure documentation",
      "Public-records and FOIA-adjacent inquiries",
      "Vendor oversight for outsourced citizen services",
      "Shared workstation accountability",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, facility and public-facing NVR streams can be indexed with the same on-premise chain-of-custody standards as sensitive workstation capture.",
      useCases: [
        "Citizen service center incident reconstruction",
        "After-hours facility access and perimeter alert review",
        "Vendor-operated site oversight for outsourced public services",
        "Oversight hearings supported by searchable camera archives",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture supports public-sector accountability on shared citizen-service desks by confirming which employee was at the keyboard during benefit, permit, or procurement actions.",
      useCases: [
        "Shared workstations in field offices where wrong-person logins undermine FOIA-adjacent inquiries",
        "Vendor-operated service centers under state oversight contracts",
        "Proportionate interval checks that avoid always-on surveillance of every idle minute",
        "Self-hosted Enterprise deployments with startup snapshots on secured agency laptops",
      ],
    },
    marketingCta: {
      titleBefore: "Ready for deployments that respect ",
      titleAccent: "civil-service rules?",
      description:
        "We'll discuss jurisdictional capture policies, notice, and network segmentation for sensitive agencies.",
    },
  },
  {
    slug: "saas",
    label: "SaaS & Technology",
    pageTitle: "SaaS & technology production evidence",
    metaDescription:
      "Production admin console session recording for SRE and trust and safety teams. DeskReplay for high-trust SaaS operators.",
    icon: Cpu,
    iconBg: "bg-teal-600",
    accentColor: "teal",
    variant: "standard",
    headline: "Turn production mysteries into reproducible facts.",
    description:
      "Customer incidents often combine CLI consoles, admin panels, and shared dashboards. DeskReplay stitches operator-visible truth across the toolchain your teams actually use during production incidents.",
    keyRisks: [
      "Production access reviews and break-glass procedures",
      "Customer trust & safety escalations",
      "QA reproduction of intermittent SaaS bugs",
      "Evidence to support SOC 2-style human-in-the-loop change reviews",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, SaaS and technology operators can index data center, co-location, and office NVR streams alongside production admin console capture.",
      useCases: [
        "Data center tailgating and after-hours access alerts during production incidents",
        "Break-glass event reviews pairing logical access logs with camera timelines",
        "Co-location and edge facility oversight for distributed infrastructure teams",
        "HQ loading dock and visitor area incidents with searchable archives",
        "SOC 2 and customer audit packages combining desktop and physical access evidence",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture adds identity context on production admin and trust-and-safety consoles when customer incidents require knowing which operator executed a break-glass change.",
      useCases: [
        "Shared NOC laptops during sev-1 incidents with rotating on-call engineers",
        "Trust and safety escalations where account actions must tie to a specific analyst",
        "SOC 2 human-in-the-loop change reviews with screen replay plus desk presence samples",
        "Enterprise triggers when production admin or internal tooling apps gain focus",
      ],
    },
    marketingCta: {
      titleBefore: "Ready to shorten ",
      titleAccent: "war rooms and blameless reviews?",
      description:
        "We'll map capture to admin consoles and internal apps, skipping unrelated developer laptops.",
    },
  },
  {
    slug: "ecommerce",
    label: "E-commerce & Retail",
    pageTitle: "E-commerce fraud & chargeback evidence",
    metaDescription:
      "Chargeback and fraud desktop session evidence for fraud analysts and finance teams. DeskReplay for retailers and e-commerce.",
    icon: ShoppingBag,
    iconBg: "bg-emerald-600",
    accentColor: "emerald",
    variant: "standard",
    headline: "Chargebacks and refund rings leave finance teams guessing.",
    description:
      "Pair order history with what fraud analysts saw when they released or blocked transactions in your OMS and fraud queues.",
    keyRisks: [
      "Payment fraud investigations",
      "Refund and promo abuse patterns",
      "Merchant collusion and insider misuse",
      "CX dispute escalation",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, store and warehouse NVR feeds can become searchable evidence for loss prevention, liability defense, and staff policy reviews.",
      useCases: [
        "Shrinkage and sweethearting investigations at POS and stockroom cameras",
        "Slip-and-fall and premises liability disputes with forensic export",
        "Staff policy compliance checks across multiple store locations",
        "Chargeback and fraud cases correlated with in-store camera timelines",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture helps fraud and finance teams confirm analysts at OMS and fraud-queue workstations match the employee who released or blocked high-risk orders.",
      useCases: [
        "Chargeback defense when remote fraud teams share credentials on hybrid schedules",
        "Insider collusion reviews pairing order decisions with identity stills",
        "Random interval checks during peak promo abuse investigations",
        "USB webcams on fulfillment supervisor PCs separate from store NVR coverage",
      ],
    },
    marketingCta: {
      titleBefore: "Ready to package ",
      titleAccent: "issuer-grade narratives?",
      description:
        "We'll scope triggers to fraud queues and OMS consoles during critical windows.",
    },
  },
  {
    slug: "adops",
    label: "Ad-Ops & Agencies",
    pageTitle: "Ad-ops trafficking & agency accountability",
    metaDescription:
      "Trafficking console session recording for DSP and social campaign disputes. DeskReplay for agencies under advertiser scrutiny.",
    icon: Megaphone,
    iconBg: "bg-teal-600",
    accentColor: "teal",
    variant: "standard",
    headline: "Stop makegoods before they become pitch decks full of apologies.",
    description:
      "Campaign launches involve synchronized updates across ad servers and walled gardens. Visual capture documents who touched pacing, targeting, and creative swaps when spend runs hot.",
    keyRisks: [
      "Campaign overspend and pacing errors",
      "Trafficking mistakes across channels",
      "Client disputes over mandate execution",
      "Offshore traffic teams & SLA governance",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, ad agencies and ad-ops teams can index creative studio, trafficking floor, and offshore delivery center NVR streams alongside trafficking console capture.",
      useCases: [
        "Creative studio asset handling and after-hours access reviews",
        "Offshore trafficking center camera oversight for client QBR evidence",
        "Client walkthrough and makegood documentation with lobby and meeting room footage",
        "Premises liability at agency offices and production facilities",
        "Cross-site search when campaigns span multiple trafficking locations",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture supports agency and offshore trafficking accountability by confirming traffickers at DSP consoles match the logins tied to client makegood disputes.",
      useCases: [
        "Offshore trafficking centers with hot-desking and client identity clauses",
        "Launch-week war rooms when multiple specialists share a trafficking workstation",
        "Enterprise triggers when ad server or walled-garden apps open during spend spikes",
        "Client QBR samples pairing pacing corrections with desk presence on interval schedules",
      ],
    },
    marketingCta: {
      titleBefore: "Ready to defend ",
      titleAccent: "your trafficking SLAs?",
      description:
        "Trigger capture on trafficking suites during launch windows, not creative laptops.",
    },
  },
  {
    slug: "logistics",
    label: "Logistics & Supply Chain",
    pageTitle: "Logistics, dispatch & carrier evidence",
    metaDescription:
      "TMS and WMS desktop session recording for damage claims and carrier disputes. DeskReplay for logistics and 3PL.",
    icon: Truck,
    iconBg: "bg-emerald-600",
    accentColor: "emerald",
    variant: "standard",
    headline: "When freight goes wrong, memories disagree.",
    description:
      "Dock supervisors and dispatchers make split-second calls in TMS consoles. Timestamped playback of TMS and WMS sessions strengthens carrier negotiations and internal RCA when freight exceptions occur.",
    keyRisks: [
      "Routing and appointment disputes",
      "Damage and shortage investigations",
      "Cross-dock misallocation",
      "3PL oversight and SLA credits",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, yard, dock door, and warehouse aisle cameras can be indexed so logistics teams prove what happened during loading, unloading, and handoff.",
      useCases: [
        "Dock appointment disputes with timestamped trailer and forklift footage",
        "Cargo damage and shortage claims supported by searchable yard archives",
        "Cross-dock misallocation proof across multiple camera angles",
        "3PL and carrier SLA reviews at high-volume fulfillment hubs",
        "Theft and unauthorized vehicle access in secured yards",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture documents which dispatcher or supervisor was at the TMS or WMS console when carrier disputes hinge on who authorized a routing or appointment change.",
      useCases: [
        "24/7 control towers with rotating staff on shared dispatch PCs",
        "Damage and shortage claims when yard cameras show the trailer but not the operator decision",
        "3PL SLA reviews with proportionate identity samples on high-liability lanes",
        "External webcams at dock supervisor stations complementing aisle NVR footage",
      ],
    },
    marketingCta: {
      titleBefore: "Ready to settle ",
      titleAccent: "exceptions with playback?",
      description:
        "We'll align capture to TMS/WMS sessions tied to your highest-liability lanes.",
    },
  },
  {
    slug: "architecture",
    label: "Architecture & Engineering",
    pageTitle: "AEC design failure & CAD forensics",
    metaDescription:
      "CAD and BIM workstation forensics for safety incidents and owner-contractor disputes. DeskReplay for AEC firms.",
    icon: HardHat,
    iconBg: "bg-teal-600",
    accentColor: "teal",
    variant: "standard",
    headline: "Give structural RCA the screen state it never recorded.",
    description:
      "Bluebeam, Revit, and bespoke calculation sheets rarely log intent. Visual archives show assessors exactly what engineers reviewed in Bluebeam, Revit, and calculation tools before sign-off.",
    keyRisks: [
      "Design failure and defect disputes",
      "RFI and submittal traceability",
      "Safety incident investigations",
      "Owner-contractor claims",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, construction site and job trailer NVR streams can be indexed alongside engineer workstation capture for safety incidents and owner-contractor disputes.",
      useCases: [
        "Site safety incident reconstruction with crane, scaffold, and perimeter cameras",
        "Owner-contractor disputes over site conditions and access",
        "Subcontractor oversight on multi-phase build programs",
        "Pairing field inspection notes with searchable site camera archives",
        "Equipment and material theft on active job sites",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture confirms which engineer was at the BIM or analysis workstation when owner-contractor disputes require pairing model state with the person who signed off.",
      useCases: [
        "Shared project PCs in job trailers during RFIs and submittal crunch periods",
        "Safety incident RCA when multiple disciplines rotate through the same review station",
        "Remote hybrid engineers on loaner laptops with startup snapshots after site VPN connect",
        "USB webcams on wide-format monitor setups where built-in cameras face the ceiling",
      ],
    },
    marketingCta: {
      titleBefore: "Ready to retire ",
      titleAccent: "\"the model looked fine here\"?",
      description:
        "Application-scoped capture around BIM and analysis stacks keeps datasets manageable.",
    },
  },
  {
    slug: "bpo-call-centers",
    label: "BPO & Call Centers",
    pageTitle: "BPO & call center compliance monitoring",
    metaDescription:
      "Agent desktop compliance and Visual Policy Enforcement for outsourced call center operations at scale. DeskReplay session recording.",
    icon: Headphones,
    iconBg: "bg-emerald-600",
    accentColor: "emerald",
    variant: "standard",
    headline: "Keep every agent desktop audit-ready, at any scale.",
    description:
      "BPO and call center environments combine high endpoint density, rapid agent turnover, and strict client SLAs. DeskReplay provides policy-scoped session recording, optional Session Audio and Webcam Capture add-ons for synchronized call and identity review, Basic OCR on Pro, Advanced OCR and Visual Policy Enforcement on Enterprise so your operations and compliance teams can verify script adherence, catch inappropriate on-screen content, and produce client-grade audit evidence without reviewing hours of footage manually.",
    keyRisks: [
      "Inappropriate or NSFW content on customer-facing endpoints",
      "Agent script and SOP deviation during regulated interactions",
      "Client data handling compliance and PII exposure",
      "Outsourced workforce policy enforcement at scale",
      "Competitor or prohibited content on managed workstations",
      "Policy alerts for political, gambling, and harassment-related imagery",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin and your sites already have NVR coverage, you can index those streams for client SLA reviews, after-hours access, and physical security alongside agent desktop capture.",
      useCases: [
        "Client QBR evidence from floor camera archives at outsourced sites",
        "After-hours access and tailgating alerts at high-volume processing centers",
        "Pairing script adherence on desktop with lobby and floor camera context",
        "Physical security incidents during high-volume processing shifts",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture helps confirm who was at the keyboard on shared or outsourced agent floors, paired with screen replay and optional Session Audio for wrong-person, moonlighting, and account-sharing investigations.",
      useCases: [
        "Hot-desk and seat-sharing programs under strict client MSAs",
        "Pairing Visual Policy alerts with identity snapshots on the same endpoint",
        "Client evidence packages with Pro interval or Enterprise trigger schedules during SLA audits",
        "External USB webcams on agent pods when laptops sit below eye-level monitors",
      ],
    },
    marketingCta: {
      titleBefore: "Ready to enforce content policies ",
      titleAccent: "across every agent desktop?",
      description:
        "Map Visual Policy Rules to your client conduct requirements and SLAs. Flagged segments route to your compliance team for review, not to your clients.",
    },
  },
  {
    slug: "realestate",
    label: "Real Estate Title",
    pageTitle: "Real estate title & escrow evidence",
    metaDescription:
      "Title and escrow workstation session evidence for wire fraud and notary disputes. DeskReplay for real estate title.",
    icon: HomeIcon,
    iconBg: "bg-emerald-600",
    accentColor: "emerald",
    variant: "standard",
    headline: "Wire fraud and missed exceptions torment title agents.",
    description:
      "Closing desks jump between title plants, wire portals, and communication tools. Policy-scoped capture on wire portals and title-production suites helps operators prove they followed wire-call procedures.",
    keyRisks: [
      "Wire fraud and socially engineered transfers",
      "Title search and exception misses",
      "Escrow balancing disputes",
      "Notary and signing-session disputes",
    ],
    cctvHighlight: {
      summary:
        "If you purchase the optional CCTV+ Plugin, office lobby and signing-room NVR streams can be indexed alongside closing desk workstation capture for wire-fraud and notary disputes.",
      useCases: [
        "Wire-release verification paired with front-desk camera timelines",
        "Notary and signing-session disputes with searchable room footage",
        "After-hours office access reviews",
        "Multi-branch title operations with centralized camera search",
        "After-hours wire desk access paired with lobby camera timelines",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture confirms closing and wire-release staff at title workstations match the employee IDs on high-value transfers, complementing lobby NVR context from CCTV+.",
      useCases: [
        "Wire fraud investigations when screen replay shows correct procedure but presence is disputed",
        "Shared closing desks with rotating notary and escrow staff",
        "Enterprise triggers when wire portal or title production apps gain focus",
        "Hybrid closers on USB webcams during remote signing weeks",
      ],
    },
    marketingCta: {
      titleBefore: "Ready to pair policies with ",
      titleAccent: "wire-verify moments?",
      description:
        "We'll focus capture on wire-release interfaces and title-production suites.",
    },
  },
  {
    slug: "security-operations",
    label: "Security Operations Centers",
    pageTitle: "Security operations center AI surveillance",
    metaDescription:
      "Optional CCTV+ Plugin for SOC teams: hot path alerts under 2 seconds, cross-camera search, and forensic export. Separate purchase on Pro or Enterprise seats.",
    icon: Shield,
    iconBg: "bg-teal-600",
    accentColor: "teal",
    variant: "featured",
    headline: "AI That Works the Night Shift Too.",
    description:
      "SOCs managing dozens of camera feeds need automation to surface the alerts that matter. The optional CCTV+ Plugin (purchased separately; Pro or Enterprise seat plan required; not on Essential) delivers hot path detection with actionable alerts within 2 seconds from live NVR streams, while cold path indexing builds a searchable archive for post-incident investigation. Average manual CCTV review takes 4+ hours. The CCTV+ Plugin is designed for teams that buy camera intelligence in addition to desktop session recording.",
    keyRisks: [
      "Real-time immobility and sleeping guard detection with sub-2-second alert latency",
      "Cross-camera search: reconstruct a person's route across 8 cameras in one query",
      "Incident queue with acknowledgment, assignment, and SLA tracking for every alert",
      "Forensic export with SHA-256 chain-of-custody ZIP for law enforcement handover",
      "MSP multi-tenancy for teams managing security across multiple client sites",
      "All AI runs on-premise, no frames sent to third-party cloud services",
    ],
    cctvHighlight: {
      summary:
        "The optional CCTV+ Plugin is built for SOC teams that purchase camera intelligence separately: real-time alerts from NVR streams, cross-camera search, incident queues, and forensic export at scale.",
      useCases: [
        "Guard immobility and unauthorized zone entry alerts in under 2 seconds",
        "Post-incident investigation across dozens of feeds with plain-English search",
        "MSP-managed camera fleets across client sites from one console",
        "Law enforcement handover with SHA-256 chain-of-custody export",
      ],
    },
    webcamHighlight: {
      summary:
        "Optional Webcam Capture verifies analysts and guards at the VMS or SOC workstation are physically at the monitoring station, while the CCTV+ Plugin watches facility NVR feeds. Endpoint camera checks deter leaving the desk during autopilot monitoring.",
      useCases: [
        "Overnight SOC shifts when immobility alerts on wall cameras need a human still at the console",
        "Confirm the logged-in VMS operator matches the person facing the feed wall",
        "Multi-monitor control rooms using external USB webcams when laptop built-ins face the wrong way",
        "Post-incident packages pairing CCTV+ hot-path alerts with operator identity snapshots",
        "Enterprise triggers when PSIM, VMS, or incident queue apps gain focus during active events",
      ],
    },
    marketingCta: {
      titleBefore: "Ready for AI that ",
      titleAccent: "works the night shift too?",
      description:
        "We will walk through hot path configuration, immobility detection thresholds, and how the incident queue fits your SOC escalation workflow.",
    },
  },
];

const topicBySlug = new Map(industryTopics.map((t) => [t.slug, t]));

export function getIndustryTopic(slug: string | undefined): IndustryTopic | undefined {
  if (!slug) return undefined;
  return topicBySlug.get(slug);
}

export const INDUSTRY_SLUGS = industryTopics.map((t) => t.slug);
